import React, { ReactElement, Ref, useContext, useRef } from 'react'
import LanguageContext from '../../Contexts/LanguageContext'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import ModalExitPromptContext, { ModalExitPromptType } from '../Modal/ModalExitPromptContext'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext'
import { GenericFooter } from '../../../../../i18n'
import { openHelpPortalByTopic } from '../../Utils'
import { servletPath } from '../../../../NonReact/common/RxJS/RxJS'
import { DocTopic } from '../../Constants/Documentation'
import Icon, { IconName } from '../Icon'
import { IconSize } from '../../../../NonReact/constants/React/Icon/Constants'
import Heading, { HeadingLevel } from '../Heading'
import Button from '../Button'
import PopperBubble from '../PopperBubble'
import P from '../P'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

/**
 * Component properties type.
 */
type Props = {
    isValidationPopperOpen: boolean
    closeModal: () => void
    serverError?: string
    docTopic?: DocTopic
    showHelpButton?: boolean
}

/**
 * Footer actions for the Add User modal.
 *
 * @param isValidationPopperOpen - Is the validation popper open.
 * @param closeModal - Close modal function.
 * @param serverError Server error string.
 * @param docTopic - Documentation topic.
 * @param noHelpButton - Should the help button be hidden.
 */
const FooterActions: React.FC<Props> = ({
    isValidationPopperOpen,
    closeModal,
    serverError,
    docTopic,
    showHelpButton = true
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(GenericFooter.NAME)

    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const elementRef: Ref<HTMLButtonElement> = useRef(null)

    const handleHelpClick = (): void => {
        if (isVarNotEmpty(docTopic)) {
            openHelpPortalByTopic(servletPath, docTopic)
        }
    }

    const handleCancel = (): void => {
        if (commonForm?.isTouched) {
            displayExitPrompt()
        } else {
            closeModal()
        }
    }

    return (
        <div>
            {serverError && (
                <div className='fa-server-error'>
                    <Icon iconName={IconName.ERRORCIRCLE} iconSize={IconSize.LARGE} />
                    <div>
                        <Heading level={HeadingLevel.TWO}>
                            {language.get(GenericFooter.GenericFooter_ServerErrorTitle)}
                        </Heading>
                        <P>{serverError}</P>
                    </div>
                </div>
            )}
            <div className='tabs-footer'>
                {showHelpButton ? (
                    <Button
                        label={language.get(GenericFooter.GenericFooter_ButtonHelpLabel)}
                        type='button'
                        onClick={handleHelpClick}
                    />
                ) : (
                    <> </>
                )}

                <Button
                    popoverRef={elementRef}
                    label={language.get(GenericFooter.GenericFooter_ButtonSubmitLabel)}
                    type='submit'
                />

                <PopperBubble
                    anchorEl={elementRef.current}
                    open={isValidationPopperOpen}
                    placement='top'
                    content={
                        <div className='validation-popper-container'>
                            <Icon iconName={IconName.ERRORCIRCLE} />
                            <span>{language.get(GenericFooter.GenericFooter_ValidationWarning)}</span>
                        </div>
                    }
                    className='validation-popper'
                />

                <Button
                    label={language.get(GenericFooter.GenericFooter_ButtonCancelLabel)}
                    onClick={handleCancel}
                    isOutlined={true}
                    type='button'
                />
            </div>
        </div>
    )
}

export default FooterActions
